import { Box, Flex, Text } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  getNoheadersStatus,
  getTableHeader,
  handleActionStepNext,
  resetMultipleIndicatorsType,
  setSelectedAreas,
  setSingleIndicatorValue,
} from 'redux/DataImportSlice';
import { getBlockInputIndicator } from 'redux/PlanPageSlice';

import colors from '../../../dataTheme';
import ActionStepFooter from '../../ActionStepFooter';
import { FieldError, FieldLabel, StepFooterRow, StepSelect } from '../../actionStepUi';

const validationSchema = Yup.object().shape({
  singleIndicatorValue: Yup.string().required('Indicator value is required'),
  columnNumericValueoofRowsIndicatorType: Yup.string().required('Column value is required'),
});

interface Properties {
  getFormValue: any;
}

const SingleIndicators = ({ getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const inputIndicator = useSelector(getBlockInputIndicator);
  const isHeader = useSelector(getNoheadersStatus);

  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);

  useEffect(() => {
    if (isHeader) {
      const array: string[] = new Array(26)
        .fill(1)
        .map((_: string, index: number) => String.fromCodePoint(65 + index));
      const newHeaders = array
        .slice(0, headerNames.length)
        .map((item: string, headerIndex: number) => {
          return { current: `${headerNames[headerIndex]}`, modified: `Column ${item}` };
        });
      setCustomHeaders(newHeaders);
    } else {
      setCustomHeaders(
        headerNames.map((item: string) => {
          return { current: `${item}`, modified: `${item}` };
        }),
      );
    }
  }, [isHeader, headerNames]);

  const handleSubmit = (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(setSingleIndicatorValue(form)).then(() => {
      dispatch(resetMultipleIndicatorsType());
      setSubmitting(false);
      dispatch(handleActionStepNext());
    });
  };

  return (
    <Box w='100%' mt={4}>
      <Formik
        initialValues={{
          singleIndicatorValue: getFormValue?.singleIndicatorValue || '',
          columnNumericValueoofRowsIndicatorType:
            getFormValue?.columnNumericValueoofRowsIndicatorType || '',
        }}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => (
          <Form style={{ width: '100%' }}>
            {inputIndicator?.length > 0 && (
              <Flex direction='column' gap={4}>
                <Box>
                  <FieldLabel>What indicator are you importing into the Block?</FieldLabel>
                  <StepSelect
                    value={values.singleIndicatorValue}
                    placeholder='Select indicator'
                    isInvalid={Boolean(errors.singleIndicatorValue)}
                    options={inputIndicator.map((item: any) => ({
                      value: item?.id,
                      label: item?.name,
                    }))}
                    onChange={(id) => setFieldValue('singleIndicatorValue', id)}
                  />
                  <ErrorMessage name='singleIndicatorValue' component={FieldError} />
                </Box>
                <Box>
                  <FieldLabel>
                    Now select the column in your table that contains the numerical values you want
                    to import
                  </FieldLabel>
                  <StepSelect
                    value={values.columnNumericValueoofRowsIndicatorType}
                    placeholder='Select…'
                    isClearable
                    isInvalid={Boolean(errors.columnNumericValueoofRowsIndicatorType)}
                    options={customHeaders.map((header: any) => ({
                      value: header.current,
                      label: header.modified,
                    }))}
                    onChange={async (role) => {
                      await setFieldValue('columnNumericValueoofRowsIndicatorType', role);
                      await dispatch(setSelectedAreas({ selectedColumnOption: role }));
                    }}
                  />
                  <ErrorMessage
                    name='columnNumericValueoofRowsIndicatorType'
                    component={FieldError}
                  />
                </Box>
              </Flex>
            )}
            {inputIndicator?.length === 0 && (
              <Text {...PLAN_TEXT.body} color={colors.danger}>
                No input indicators
              </Text>
            )}

            <StepFooterRow>
              <ActionStepFooter
                nextButtonName='Continue'
                prevButtonName='Previous'
                isSubmitting={isSubmitting}
                disableNext={inputIndicator?.length === 0}
              />
            </StepFooterRow>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default SingleIndicators;
